import { Formik } from 'formik';
import { useCallback, useContext } from 'react';

import { customersApi } from 'src/app/services/Customers/Customers.service';
import {
  customerLeadContactTabValidationSchema,
  type ICustomerLeadContactTabFormValues,
  toCustomerLeadContactTabInitialValues,
} from 'src/components/customers/steps/Contact/CustomerLeadStepContactTab.validation';
import { CustomerLeadStepContactTabContent } from 'src/components/customers/steps/Contact/CustomerLeadStepContactTabContent';
import { getContactsDiff } from 'src/helpers/getContactsDiff';
import { toApiContact } from 'src/helpers/toApiContact';
import { CustomersContext } from 'src/pages/CustomersPage/CustomersPage.context';

type CustomerLeadStepContactTabProps = {
  customerProductLeadId: string;
  onSubmit: () => boolean;
};

export function CustomerLeadStepContactTab({
  customerProductLeadId,
  onSubmit,
}: Readonly<CustomerLeadStepContactTabProps>) {
  const { customerLeadProduct, updateCustomerLeadProduct } = useContext(CustomersContext);

  const [, partialUpdateCustomerProductLead] =
    customersApi.usePartialUpdateCustomerProductLeadMutation(customerProductLeadId);
  const [, manualGetCustomerProductLead] = customersApi.useManualGetCustomerProductLeadQuery(customerProductLeadId);

  const [, createCustomerLeadContact] = customersApi.useCreateCustomerLeadContactMutation(customerProductLeadId);
  const updateCustomerLeadContact = customersApi.useUpdateCustomerLeadContactMutation(customerProductLeadId);
  const deleteCustomerLeadContact = customersApi.useDeleteCustomerLeadContactMutation(customerProductLeadId);

  const save = useCallback(
    async (newValues: ICustomerLeadContactTabFormValues, submit: boolean) => {
      const { addedContacts, updatedContacts, removedContacts } = getContactsDiff(
        newValues.contacts,
        customerLeadProduct?.contacts ?? []
      );

      await Promise.all([
        ...addedContacts.map((addedContact) =>
          createCustomerLeadContact({
            data: {
              ...toApiContact(addedContact),
            },
          })
        ),
        ...updatedContacts.map((updatedContact) => updateCustomerLeadContact(toApiContact(updatedContact))),
        ...removedContacts.map((removedContactId) => deleteCustomerLeadContact(removedContactId)),
      ]);

      const [updatedCustomerProductLead] = await Promise.all([
        // Do additional request to get final version of customer lead
        manualGetCustomerProductLead(),
        ...(submit ? [partialUpdateCustomerProductLead({ data: { contactList: { submit: true } } })] : []),
      ]);

      updateCustomerLeadProduct(updatedCustomerProductLead.data);
    },
    [
      customerLeadProduct,
      createCustomerLeadContact,
      updateCustomerLeadContact,
      deleteCustomerLeadContact,
      partialUpdateCustomerProductLead,
      manualGetCustomerProductLead,
      updateCustomerLeadProduct,
    ]
  );

  const handleSubmit = useCallback(
    async (submitValues: ICustomerLeadContactTabFormValues) => {
      await save(submitValues, true);

      return onSubmit();
    },
    [save, onSubmit]
  );

  const handleUpdate = useCallback((newValues: ICustomerLeadContactTabFormValues) => save(newValues, false), [save]);

  if (!customerLeadProduct) {
    return null;
  }

  return (
    <Formik<ICustomerLeadContactTabFormValues>
      initialValues={toCustomerLeadContactTabInitialValues(customerLeadProduct)}
      validationSchema={customerLeadContactTabValidationSchema}
      validateOnMount={true}
      enableReinitialize={true}
      onSubmit={handleSubmit}
    >
      {(formikProps) => <CustomerLeadStepContactTabContent formikProps={formikProps} onUpdate={handleUpdate} />}
    </Formik>
  );
}
